import React, { useState } from "react";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { DashboardEditorProps } from "components/ComponentType";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { SankeyDiagramNode, SankeyDiagramProps } from "../sankey-types";
import XYPositionsEditor from "./XYPositionsEditor";

interface RowEditEvent {
  newData: RowData;
  data: RowData;
  index: number;
  originalEvent: React.SyntheticEvent;
}

interface RowData {
  id: string;
  title: string;
  reference: boolean;
}

const NodesEditor = ({ config, onConfigChange }: DashboardEditorProps<SankeyDiagramProps>) => {
  const [xyPositionsEditorVisible, setXYPositionsEditorVisible] = useState<boolean>(false);
  const [selectedNode, setSelectedNode] = useState<SankeyDiagramNode | null>(null);

  const editXYPositions = (node: SankeyDiagramNode) => {
    setSelectedNode(node);
    setXYPositionsEditorVisible(true);
  };

  const textEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        value={options.value || ""}
        onChange={e => options.editorCallback && options.editorCallback(e.target.value as string)}
        style={{ width: "100%" }}
      />
    );
  };

  const renderXYPositionsCell = (rowData: SankeyDiagramNode) => {
    if (config.overridePositions) {
      return (
        <Button
          type="button"
          icon="pi pi-external-link"
          className="p-button-rounded p-button-secondary p-button-text pl-5"
          onClick={() => editXYPositions(rowData)}
        />
      );
    }
    return null;
  };

  const onRowEditComplete = (e: RowEditEvent) => {
    const nodesToUpdate = [...config.graph.nodes!];
    const { newData } = e;
    const updatedNodes = nodesToUpdate.map(node => {
      if (node.id === newData.id) {
        return { ...node, ...newData };
      }
      return node;
    });

    const updatedConfig = {
      ...config,
      graph: { ...config.graph, nodes: updatedNodes },
    };
    onConfigChange(updatedConfig);
  };

  const renderReferenceCell = (rowData: SankeyDiagramNode) => {
    return (
      <InputSwitch
        checked={rowData.reference}
        onChange={e => {
          const nodesToUpdate = [...config.graph.nodes!];
          const updatedNodes = nodesToUpdate.map(node => {
            if (node.id === rowData.id) {
              return { ...node, reference: e.value };
            }
            return node;
          });

          const updatedConfig = {
            ...config,
            graph: { ...config.graph, nodes: updatedNodes },
          };
          onConfigChange(updatedConfig);
        }}
      />
    );
  };

  return (
    <div>
      <DataTable
        value={config.graph.nodes}
        editMode="row"
        onRowEditComplete={onRowEditComplete}
        dataKey="id"
      >
        <Column field="id" header="ID" />
        <Column field="title" header="Title" editor={textEditor} />
        <Column field="reference" header="Reference" body={renderReferenceCell} />
        <Column
          field="xyPositions"
          header={config.overridePositions ? "XY Positions" : ""}
          body={renderXYPositionsCell}
          style={{ width: "10%" }}
        />
        <Column rowEditor align="center" style={{ width: "10%" }} />
      </DataTable>
      <Dialog
        visible={xyPositionsEditorVisible}
        onHide={() => setXYPositionsEditorVisible(false)}
        header="Edit XY Positions"
        modal
        draggable
        resizable
        className="w-4"
      >
        <XYPositionsEditor
          config={config}
          onConfigChange={onConfigChange}
          selectedNode={selectedNode}
        />
      </Dialog>
    </div>
  );
};

export default NodesEditor;
